
import { apiClient } from './client';

export interface UserBalance {
  credits: number;
  subscription_tier: string;
  membership_type?: string;
  subscription_expires_at?: string;
}

export interface ChatSession {
  session_id: string;
  character_id: number;
  user_id: number;
  is_active: boolean;
  created_at: string;
  last_message_at?: string;
  message_count: number;
  // Access control flags (backend augmented)
  can_chat?: boolean;
  cant_chat_reason_code?: 'subscription_required' | 'blocked' | null;
  show_reason?: boolean;
}

export interface RecentChat {
  character_id: number;
  character?: {
    id: number;
    name: string;
    avatar_url: string;
  };
  last_message?: string;
  last_message_at: string;
  message_count: number;
}

export interface Message {
  id: number;
  content: string;
  is_from_character: boolean;
  created_at: string;
  character_id: number;
  user_id: number;
  message_type?: string;
  media_url?: string;
}

export interface ChatMessage {
  id: number;
  content: string;
  is_user: boolean;
  created_at: string;
  character_id: number;
  user_id: number;
  message_type?: string;
}

export interface ChatSessionWithDetails {
  session_id: string;
  character_id: number;
  character_name: string;
  character_avatar: string;
  character_gallery?: Array<{
    id: number;
    url: string;
    thumbnail_url?: string;
    media_type?: 'image' | 'video';
    is_profile_image: boolean;
    is_fsk18: boolean;
    should_blur?: boolean;
  }>;
  avatar_metadata?: {
    is_fsk18?: boolean;
    should_blur?: boolean;
    media_type?: 'image' | 'video';
    thumbnail_url?: string;
  };
  is_online: boolean;
  is_creator: boolean;  // Add creator flag
  last_message?: string;
  last_message_at?: string;
  last_message_from_character: boolean;
  started_at: string;
  message_count: number;
  unread_count: number;
  character_typing?: boolean;
  is_archived?: boolean;
  archived_at?: string | null;
  // Access control flags
  can_chat?: boolean;
  cant_chat_reason_code?: 'subscription_required' | 'blocked' | null;
  show_reason?: boolean;
}

export const chatAPI = {
  getBalance: () => apiClient.get<UserBalance>('/chat/balance'),
  
  getSessions: (skip = 0, limit = 20, includeArchived = false) => 
    apiClient.get<ChatSessionWithDetails[]>(`/chat/sessions?skip=${skip}&limit=${limit}&include_archived=${includeArchived ? 'true' : 'false'}`),
  
  createSession: (characterId: number) => 
    apiClient.post<ChatSession>(`/chat/session/${characterId}`, {}),
  
  sendMessage: (data: {
    session_id: string;
    content: string;
    message_type?: string;
    character_id?: number;
  }) => apiClient.post<Message>('/chat/send', data),
  
  // getMessages is handled by getHistory endpoint

  getHistory: (sessionId: string, skip = 0, limit = 20) => 
    apiClient.get<{ messages: ChatMessage[] }>(`/chat/history/${sessionId}?skip=${skip}&limit=${limit}`),

  sendImage: (data: {
    session_id: string;
    file: File;
    caption?: string;
  }) => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('session_id', data.session_id);
    if (data.caption) {
      formData.append('caption', data.caption);
    }
    return apiClient.post<Message>('/chat/send/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  markMessageAsRead: (messageId: number) =>
    apiClient.post(`/chat/messages/${messageId}/read`),
  
  markAllMessagesAsRead: (sessionId: string) =>
    apiClient.post(`/chat/sessions/${sessionId}/read-all`),
  
  endSession: (sessionId: string) =>
    apiClient.post(`/chat/session/${sessionId}/end`),

  archiveSession: (sessionId: string) =>
    apiClient.post(`/chat/session/${sessionId}/archive`),

  unarchiveSession: (sessionId: string) =>
    apiClient.post(`/chat/session/${sessionId}/unarchive`),
  getSessionStatus: (sessionId: string) =>
    apiClient.get<{ 
      session_id: string;
      chat_status: any;
      typing_statuses: { users: Record<string, any>; characters: Record<string, any> };
      timestamp: string;
    }>(`/chat/session/${sessionId}/status`),

  // Recent chats endpoint not implemented in backend yet
  // getRecentChats: () => apiClient.get<RecentChat[]>('/chat/recent'),
  
  sendImageMessage: (sessionId: string, characterId: number, formData: FormData) => {
    // Add session_id and character_id to formData if not already present
    if (!formData.has('session_id')) {
      formData.append('session_id', sessionId);
    }
    if (!formData.has('character_id')) {
      formData.append('character_id', characterId.toString());
    }
    
    return apiClient.post<Message>('/chat/send-image', formData);
  },
};
